import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { ClientContact, CreateContactDraft } from "../types";
import styles from "./ClientContactField.module.css";

export interface ContactFormProps {
  /** Persists the draft (create or update); returns the saved contact. */
  saveContact: (draft: CreateContactDraft) => Promise<ClientContact>;
  /** Pre-fills the fields for edit mode; omit for a blank create form. */
  initial?: CreateContactDraft;
  heading?: string;
  submitLabel?: string;
  /** Called with the saved contact on success. */
  onSaved: (contact: ClientContact) => void;
  onCancel: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Inline contact form shared by the New Job combobox (create) and the
 * /clients contacts modal (create + edit) — one set of fields, validation,
 * and error reporting everywhere a contact is captured.
 *
 * Rendered as a `<div>` (not a `<form>`) because in the New Job form it is
 * nested inside the parent request form; Enter submits via the key handler.
 */
export function ContactForm({
  saveContact,
  initial,
  heading = "Create a new contact",
  submitLabel = "Create Contact",
  onSaved,
  onCancel,
}: ContactFormProps) {
  const [firstName, setFirstName] = useState(initial?.firstName ?? "");
  const [lastName, setLastName] = useState(initial?.lastName ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const submit = async () => {
    if (submitting) return;
    // Without a name the server's display name falls back to "Unnamed contact",
    // which is not something anyone can pick out of a list later.
    if (!firstName.trim() && !lastName.trim()) {
      setError("Enter a first or last name.");
      nameRef.current?.focus();
      return;
    }
    if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
      setError("Enter a valid email, or leave it blank.");
      return;
    }
    setSubmitting(true);
    try {
      const contact = await saveContact({ firstName, lastName, phone, email, notes });
      onSaved(contact);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Contact save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !(event.target instanceof HTMLTextAreaElement)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <div className={styles.create} role="group" aria-label={heading} onKeyDown={onKeyDown}>
      <span className={styles.createHeading}>{heading}</span>
      <div className={styles.pair}>
        <label className={styles.createField}>
          <span className={styles.createLabel}>First name</span>
          <input
            ref={nameRef}
            className={styles.input}
            name="contactFirstName"
            value={firstName}
            autoComplete="off"
            onChange={(event) => {
              setFirstName(event.currentTarget.value);
              if (error) setError("");
            }}
          />
        </label>
        <label className={styles.createField}>
          <span className={styles.createLabel}>Last name</span>
          <input
            className={styles.input}
            name="contactLastName"
            value={lastName}
            autoComplete="off"
            onChange={(event) => {
              setLastName(event.currentTarget.value);
              if (error) setError("");
            }}
          />
        </label>
      </div>
      <div className={styles.pair}>
        <label className={styles.createField}>
          <span className={styles.createLabel}>Phone</span>
          <input
            className={styles.input}
            name="contactPhone"
            value={phone}
            autoComplete="off"
            onChange={(event) => setPhone(event.currentTarget.value)}
          />
        </label>
        <label className={styles.createField}>
          <span className={styles.createLabel}>Email</span>
          <input
            className={styles.input}
            name="contactEmail"
            type="email"
            value={email}
            autoComplete="off"
            onChange={(event) => {
              setEmail(event.currentTarget.value);
              if (error) setError("");
            }}
          />
        </label>
      </div>
      <label className={styles.createField}>
        <span className={styles.createLabel}>Notes</span>
        <textarea
          className={styles.textarea}
          name="contactNotes"
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
        />
      </label>
      {error ? (
        <span role="alert" className={styles.createError}>
          {error}
        </span>
      ) : null}
      <div className={styles.actions}>
        <button type="button" className={styles.cancel} onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.submit}
          onClick={() => void submit()}
          disabled={submitting}
        >
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}
